import { create } from 'zustand';
import {
  type AuthUser,
  fetchMe,
  getAuthToken,
  login as apiLogin,
  logoutApi,
  register as apiRegister,
  setAuthToken,
  patchMe,
} from '../services/api';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  initialized: boolean;
  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; fullName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (u: AuthUser | null) => void;
  setMode: (mode: 'novice' | 'pro') => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  initialized: false,

  setUser: (u) => set({ user: u }),

  init: async () => {
    if (!getAuthToken()) {
      set({ initialized: true });
      return;
    }
    set({ loading: true });
    try {
      const u = await fetchMe();
      set({ user: u, initialized: true, loading: false });
    } catch {
      setAuthToken(null);
      set({ user: null, initialized: true, loading: false });
    }
  },

  login: async (email, password) => {
    set({ loading: true });
    try {
      const r = await apiLogin(email, password);
      setAuthToken(r.token);
      set({ user: r.user, loading: false, initialized: true });
    } catch (e) {
      set({ loading: false });
      throw e;
    }
  },

  register: async (data) => {
    set({ loading: true });
    try {
      const r = await apiRegister(data);
      setAuthToken(r.token);
      set({ user: r.user, loading: false, initialized: true });
    } catch (e) {
      set({ loading: false });
      throw e;
    }
  },

  logout: async () => {
    try { await logoutApi(); } catch {}
    setAuthToken(null);
    set({ user: null });
  },

  setMode: async (mode) => {
    // optimistic
    set((s) => ({ user: s.user ? { ...s.user, mode } : s.user }));
    try {
      const updated = await patchMe({ mode });
      set({ user: updated });
    } catch (err) {
      console.error('setMode failed', err);
      // rollback by re-fetching
      try {
        const u = await fetchMe();
        set({ user: u });
      } catch {}
    }
  },
}));
